import Qrious from 'qrious'
import screenfull from 'screenfull'
import Socket from '@/socket'
import EventBus from '@/eventBus'
import PresentationApi from '@/api/presentation'
import { isFeatureEnabled } from '@/vuex/modules/featureToggles.module'
import config from '@/config'
import { SET_CURRENT_SLIDE_MODE, FULL_SCREEN_MODE, WINDOWED_MODE } from '@/vuex/types'

const MODULE_NAME = 'remoteControlLink'
const SEND_EFFECT_ACTION_NAME = 'sendEffectAction'
const TOGGLE_QR_CODE_ACTION_NAME = 'toggleQRCode'
const TOGGLE_FULL_SCREEN_ACTION_NAME = 'toggleFullScreen'
const TOGGLE_INSTRUCTION_BAR_ACTION_NAME = 'toggleInstructionBar'
const TOGGLE_QUIZ_MUSIC_ACTION_NAME = 'toggleQuizMusic'
const CONTROL_AUDIO_ACTION_NAME = 'controlAudio'
const NAVIGATE_SLIDE_ACTION_NAME = 'navigateSlide'

// for outside module calling and referencing
export const SEND_EFFECT_ACTION = `${MODULE_NAME}/${SEND_EFFECT_ACTION_NAME}`
export const TOGGLE_QR_CODE = `${MODULE_NAME}/${TOGGLE_QR_CODE_ACTION_NAME}`
export const TOGGLE_FULL_SCREEN = `${MODULE_NAME}/${TOGGLE_FULL_SCREEN_ACTION_NAME}`
export const TOGGLE_INSTRUCTION_BAR = `${MODULE_NAME}/${TOGGLE_INSTRUCTION_BAR_ACTION_NAME}`
export const TOGGLE_QUIZ_MUSIC = `${MODULE_NAME}/${TOGGLE_QUIZ_MUSIC_ACTION_NAME}`
export const CONTROL_AUDIO = `${MODULE_NAME}/${CONTROL_AUDIO_ACTION_NAME}`
export const NAVIGATE_SLIDE = `${MODULE_NAME}/${NAVIGATE_SLIDE_ACTION_NAME}`

const navigateKeyMap = {
  next: 'ArrowRight',
  prev: 'ArrowLeft',
}

const navigateKeyCodeMap = {
  next: 39,
  prev: 37,
}

const _state = {
  isLoading: false,
}

const actions = {
  getRemoteLink: async ({ commit }, presentation) => {
    if (presentation.remoteAccessCode) return
    commit('setIsLoading', true)
    const { remoteAccessCode } = await PresentationApi.generateRemoteAccessCode({ presentationId: presentation.id })
    commit('updatePresentation', { remoteAccessCode }, { root: true })
    commit('setIsLoading', false)
  },
  resetRemoteLink: async ({ commit }, presentationId) => {
    commit('setIsLoading', true)
    const { remoteAccessCode } = await PresentationApi.revokeRemoteAccessCode({ presentationId })
    commit('updatePresentation', { remoteAccessCode }, { root: true })
    commit('setIsLoading', false)
  },
  // eslint-disable-next-line no-unused-vars
  [NAVIGATE_SLIDE_ACTION_NAME]: async ({ getters }, { source, navigate }) => {
    const key = navigateKeyMap[navigate]
    const keyCode = navigateKeyCodeMap[navigate]
    if (!key || !keyCode || !getters.canUseRemoteControl) return

    // simulate keyboard event
    EventBus.$emit('onKeyboard', {
      code: key,
      key,
      keyCode,
      target: {
        tagName: 'BODY',
      },
    })
  },
  // source can be remote control or any other source
  // eslint-disable-next-line no-unused-vars
  [SEND_EFFECT_ACTION_NAME]: async (_, { effect, source }) => {},
  // eslint-disable-next-line no-unused-vars
  [TOGGLE_QR_CODE_ACTION_NAME]: async ({ dispatch, commit }, { value, source }) => {
    // dispatch('toggleQRCodePanelAction', value, { root: true })
    // Change to commit to avoid cicularly function call
    commit('setQRCodeVisible', value, { root: true })
  },
  // eslint-disable-next-line no-unused-vars
  [TOGGLE_FULL_SCREEN_ACTION_NAME]: async ({ dispatch }, { value, source }) => {
    const mode = value ? FULL_SCREEN_MODE : WINDOWED_MODE
    if (value) {
      screenfull.request().catch(() => {})
    } else {
      screenfull.exit().catch(() => {})
    }
    dispatch(SET_CURRENT_SLIDE_MODE, mode, { root: true })
  },
  // eslint-disable-next-line no-unused-vars
  [TOGGLE_INSTRUCTION_BAR_ACTION_NAME]: async ({ dispatch, rootState }, { value, source }) => {
    const presentationId = rootState?.presentation?.id
    if (!presentationId) return
    dispatch('updatePresentationByIdAction', {
      id: presentationId,
      payload: {
        hideInstructionBar: !value,
        hideIntroBarDocument: !value,
        socketId: Socket.getSocketId(),
      },
    }, { root: true })
  },
  // eslint-disable-next-line no-unused-vars
  [TOGGLE_QUIZ_MUSIC_ACTION_NAME]: async ({ dispatch, rootState }, { value, source }) => {
    const presentationId = rootState?.presentation?.id
    if (!presentationId) return
    dispatch('updatePresentationByIdAction', {
      id: presentationId,
      payload: {
        isEnableQuizMusic: value,
      },
    }, { root: true })
    EventBus.$emit('play-quiz-music', value)
  },
  // eslint-disable-next-line no-unused-vars
  [CONTROL_AUDIO_ACTION_NAME]: async (_, { value, source }) => {},
}

const _getters = {
  canUseRemoteControl: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-remote-control')
  },
  isLoadingRemoteLink: (state) => {
    return state.isLoading
  },
  remoteAccessCode: (_, __, rootState) => {
    const { remoteAccessCode } = rootState.presentation
    if (!remoteAccessCode) return ''
    return remoteAccessCode
  },
  remoteLink: (_, getters) => {
    const { remoteAccessCode } = getters
    if (!remoteAccessCode) return ''
    return `${config.remoteControlUrl}/${remoteAccessCode}`
  },
  remoteLinkQrSource: (_, getters) => {
    const { remoteLink } = getters
    if (!remoteLink) return ''
    return `${remoteLink}?source=qr_code`
  },
  remoteLinkCopySource: (_, getters) => {
    const { remoteLink } = getters
    if (!remoteLink) return ''
    return `${remoteLink}?source=copy_link`
  },
  remoteLinkQrCode: (_, getters) => {
    const { remoteLinkQrSource } = getters
    if (!remoteLinkQrSource) return ''

    const qr = new Qrious({
      value: remoteLinkQrSource,
      size: 800,
      level: 'H',
    })
    return qr.toDataURL()
  },
}

const _mutations = {
  setIsLoading: (state, payload) => {
    state.isLoading = payload
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations: _mutations,
}
